import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const REQUEST_TYPES = [
  { id: "preventivo", label: "PREVENTIVO" },
  { id: "domanda", label: "DOMANDA RAPIDA" },
  { id: "call", label: "CALL GRATUITA · 30 MIN" },
];

const STAGES = [
  "Idea, sto esplorando",
  "Sviluppo iniziale, brand da definire",
  "Serve la grafica a breve",
  "Pronto a partire subito",
];

const inputCls =
  "w-full border border-neutral-300 bg-white px-4 py-3 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-neutral-400 focus:border-ink";
const labelCls = "mb-2 block font-mono text-[10px] tracking-[0.2em] text-neutral-500";

const INITIAL = {
  request_type: "preventivo",
  name: "",
  email: "",
  company: "",
  stage: "",
  website: "",
  message: "",
  call_date: "",
  call_slot: "",
};

export default function ContactForm() {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.request_type !== "call" || !form.call_date) {
      setSlots([]);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    setForm((f) => ({ ...f, call_slot: "" }));
    fetch(`${API}/api/availability?date=${form.call_date}`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setSlots(data.slots || []);
        setCalendarConnected(Boolean(data.calendar_connected));
      })
      .catch(() => {
        if (!cancelled) {
          setSlots([]);
          toast.error("Non riesco a leggere la disponibilità", {
            description: "Riprova tra poco o scrivimi via WhatsApp.",
          });
        }
      })
      .finally(() => !cancelled && setSlotsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [form.request_type, form.call_date]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.request_type === "call" && (!form.call_date || !form.call_slot)) {
      toast.error("Scegli data e orario per prenotare la call");
      return;
    }
    setStatus("sending");
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : v])
      );
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Invio non riuscito");
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      toast.error("Invio non riuscito", {
        description:
          err.message && err.message !== "Invio non riuscito"
            ? err.message
            : "Riprova tra poco oppure scrivimi direttamente via email o WhatsApp.",
      });
    }
  };

  if (status === "success") {
    const niceDate = form.call_date
      ? new Date(`${form.call_date}T00:00:00`).toLocaleDateString("it-IT", {
          weekday: "long",
          day: "numeric",
          month: "long",
        })
      : null;
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        data-testid="contact-form-success"
        className="flex h-full flex-col items-start justify-center gap-4 p-6 sm:p-10"
      >
        <span className="flex h-12 w-12 items-center justify-center bg-cyan-brand">
          <Check className="h-6 w-6 text-ink" />
        </span>
        <h3 className="font-display text-2xl font-bold tracking-tight">
          Richiesta inviata!
        </h3>
        <p className="max-w-md text-sm leading-relaxed text-neutral-600">
          {form.request_type === "call" && niceDate
            ? `Hai richiesto la call gratuita per ${niceDate} alle ${form.call_slot}. Ti scrivo entro 24h per confermare e inviarti il link.`
            : "Grazie per avermi raccontato il tuo progetto: ti rispondo personalmente entro 24 ore."}
        </p>
        <button
          data-testid="contact-form-reset-button"
          onClick={() => {
            setForm(INITIAL);
            setStatus("idle");
          }}
          className="border border-ink px-4 py-2.5 font-mono text-xs tracking-wider transition-colors duration-300 hover:bg-ink hover:text-paper"
        >
          INVIA UN'ALTRA RICHIESTA
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="contact-form" className="p-6 sm:p-10">
      <span className={labelCls}>DI COSA HAI BISOGNO? *</span>
      <div className="flex flex-wrap gap-2" data-testid="contact-request-types">
        {REQUEST_TYPES.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`contact-type-${t.id}`}
            onClick={() => set("request_type", t.id)}
            className={`border px-4 py-2.5 font-mono text-xs tracking-wider transition-colors duration-300 ${
              form.request_type === t.id
                ? "border-ink bg-ink text-paper"
                : "border-neutral-300 text-neutral-600 hover:border-ink hover:text-ink"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className={labelCls}>NOME *</label>
          <input
            id="contact-name"
            data-testid="contact-name-input"
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Il tuo nome"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="contact-email" className={labelCls}>EMAIL *</label>
          <input
            id="contact-email"
            data-testid="contact-email-input"
            required
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="nome@azienda.it"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="contact-company" className={labelCls}>AZIENDA / BRAND</label>
          <input
            id="contact-company"
            data-testid="contact-company-input"
            value={form.company}
            onChange={(e) => set("company", e.target.value)}
            placeholder="Nome del tuo brand"
            className={inputCls}
          />
        </div>
        <div>
          <label htmlFor="contact-stage" className={labelCls}>FASE DEL PROGETTO</label>
          <select
            id="contact-stage"
            data-testid="contact-stage-select"
            value={form.stage}
            onChange={(e) => set("stage", e.target.value)}
            className={`${inputCls} ${form.stage ? "" : "text-neutral-400"}`}
          >
            <option value="">Seleziona la fase</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="contact-website" className={labelCls}>SITO WEB O PROFILO SOCIAL</label>
          <input
            id="contact-website"
            data-testid="contact-website-input"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            placeholder="https://"
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="contact-message" className={labelCls}>MESSAGGIO</label>
          <textarea
            id="contact-message"
            data-testid="contact-message-input"
            rows={4}
            value={form.message}
            onChange={(e) => set("message", e.target.value)}
            placeholder="Raccontami in due righe prodotto, obiettivi e tempistiche"
            className={`${inputCls} resize-none`}
          />
        </div>
      </div>

      {form.request_type === "call" && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.4 }}
          className="mt-8 overflow-hidden border border-neutral-300 bg-paper p-5"
          data-testid="contact-call-picker"
        >
          <span className={labelCls}>PRENOTA LO SLOT — CONSULENZA GRATUITA 30 MIN *</span>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="contact-call-date" className={labelCls}>GIORNO</label>
              <input
                id="contact-call-date"
                type="date"
                min={today}
                data-testid="contact-call-date-input"
                value={form.call_date}
                onChange={(e) => set("call_date", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <span className={labelCls}>
                ORARIO (CET){calendarConnected ? " — SOLO SLOT DAVVERO LIBERI" : ""}
              </span>
              {!form.call_date ? (
                <p className="font-mono text-xs text-neutral-400" data-testid="contact-slots-hint">
                  Scegli prima il giorno
                </p>
              ) : slotsLoading ? (
                <p className="flex items-center gap-2 font-mono text-xs text-neutral-500" data-testid="contact-slots-loading">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> CONTROLLO DISPONIBILITÀ…
                </p>
              ) : slots.length === 0 ? (
                <p className="font-mono text-xs text-neutral-500" data-testid="contact-slots-empty">
                  In questo giorno non ci sono slot: prenoto call da lunedì a venerdì
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="contact-slots">
                  {slots.map((s) => (
                    <button
                      key={s.time}
                      type="button"
                      disabled={!s.available}
                      data-testid={`contact-slot-${s.time.replace(":", "")}`}
                      onClick={() => set("call_slot", s.time)}
                      className={`border px-3 py-2 font-mono text-xs tracking-wider transition-colors duration-300 ${
                        !s.available
                          ? "cursor-not-allowed border-neutral-200 text-neutral-300 line-through"
                          : form.call_slot === s.time
                            ? "border-cyan-brand bg-cyan-brand text-ink"
                            : "border-neutral-300 bg-white text-neutral-600 hover:border-ink hover:text-ink"
                      }`}
                    >
                      {s.time}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-mono text-[10px] tracking-wider text-neutral-500">
          RISPOSTA ENTRO 24H · NESSUN IMPEGNO
        </p>
        <button
          type="submit"
          data-testid="contact-submit-button"
          disabled={status === "sending"}
          className="flex items-center justify-center gap-2 bg-ink px-8 py-3.5 font-mono text-xs tracking-wider text-paper transition-colors duration-300 hover:bg-magenta-brand disabled:opacity-60"
        >
          {status === "sending" ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          {status === "sending" ? "INVIO IN CORSO…" : "INVIA LA RICHIESTA"}
        </button>
      </div>
    </form>
  );
}
